/**
 * ClientSidePanel — A right-side Sheet that shows rich Finance System information
 * for a client when their name is clicked anywhere in the application.
 *
 * In Phase 1 this reads directly from the passed `client` prop (mock data).
 * In Phase 2 it will call GET /api/clients/{id} on open.
 */

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  AlertTriangle,
  MapPin,
  CheckCircle2,
  ChevronDown,
  User,
  CreditCard,
  Building2,
  Hash,
  Info,
  Receipt,
  AlertCircle,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import type { Client } from '@/types';
import { fetchClientFinancials, fetchClientBillingSummary, getDataMode } from '@/lib/data-service';
import type { ClientBillingSummary } from '@/lib/data-service';

// ── Helpers ──────────────────────────────────────────────────────────────

function formatCurrencyValue(amount: number | null | undefined, currencyCode?: string | null): string {
  const code = currencyCode || 'GBP';
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount ?? 0);
  } catch {
    // Invalid currency code from Finance System — fall back to GBP
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount ?? 0);
  }
}

function getClientInitials(name: string | null | undefined): string {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    || '?';
}

function getAccountStatusBadge(status: Client['accountStatus']) {
  switch (status) {
    case 'active':
      return <Badge variant="outline" className="bg-success-50 text-success-700 border-success-300 text-xs">Active</Badge>;
    case 'on-stop':
      return <Badge variant="outline" className="bg-error-50 text-error-700 border-error-300 text-xs">On Stop</Badge>;
    case 'suspended':
      return <Badge variant="outline" className="bg-warning-50 text-warning-700 border-warning-300 text-xs">Suspended</Badge>;
    case 'prospect':
      return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300 text-xs">Prospect</Badge>;
    case 'closed':
      return <Badge variant="outline" className="bg-muted text-muted-foreground text-xs">Closed</Badge>;
    default:
      return null;
  }
}

const CONTACT_ROLE_LABELS: Record<string, string> = {
  primary: 'Primary',
  billing: 'Billing',
  'accounts-payable': 'Accounts Payable',
  legal: 'Legal',
  technical: 'Technical',
};

// ── Address helpers ───────────────────────────────────────────────────────

function addressesMatch(a?: Client['invoiceAddress'], b?: Client['deliveryAddress']): boolean {
  if (!a || !b) return false;
  return (
    a.line1 === b.line1 &&
    (a.line2 || '') === (b.line2 || '') &&
    a.city === b.city &&
    a.postcode === b.postcode
  );
}

function AddressBlock({ address }: { address: NonNullable<Client['invoiceAddress']> }) {
  return (
    <div className="text-sm space-y-0.5">
      <div>{address.line1}</div>
      {address.line2 && <div>{address.line2}</div>}
      <div>
        {address.city}
        {address.county ? `, ${address.county}` : ''}
      </div>
      <div>{address.postcode}</div>
      <div>{address.country}</div>
    </div>
  );
}

// ── Payment term formatters ───────────────────────────────────────────────

function formatDueDate(type: Client['dueDateType'], days?: number): string {
  switch (type) {
    case 'days-from-invoice':
      return `${days ?? 0} days from invoice`;
    case 'end-of-following-month':
      return 'End of following month';
    case 'day-of-following-month':
      return `Day ${days ?? ''} of following month`;
    case 'days-after-end-of-month':
      return `${days ?? 0} days after end of month`;
    default:
      return '—';
  }
}

// ── Row helper ───────────────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start gap-4 text-sm">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}

// ── Section heading ──────────────────────────────────────────────────────

function SectionHeading({ label }: { label: string }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground pt-2">
      {label}
    </p>
  );
}

// ── Main component ───────────────────────────────────────────────────────

export interface ClientSidePanelProps {
  /** The client to display, or null/undefined when closed */
  client: Client | null | undefined;
  /** Whether the sheet is open */
  open: boolean;
  /** Callback to close the sheet */
  onClose: () => void;
  /** Company ID — needed in live mode to call the Financials pass-through API */
  companyId?: number;
}

export function ClientSidePanel({ client, open, onClose, companyId }: ClientSidePanelProps) {
  const [financeCodesOpen, setFinanceCodesOpen] = useState(false);
  const [enrichedClient, setEnrichedClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [billingSummary, setBillingSummary] = useState<ClientBillingSummary | null>(null);
  const [billingSummaryLoading, setBillingSummaryLoading] = useState(false);

  useEffect(() => {
    if (!open || !client?.id) {
      setEnrichedClient(null);
      setError(null);
      setBillingSummary(null);
      return;
    }

    const mode = getDataMode();
    if (mode !== 'live') {
      // Demo/tour mode — client prop already has all data
      setEnrichedClient(client);
      // Still fetch billing summary (uses mock data in demo/tour mode)
      setBillingSummaryLoading(true);
      fetchClientBillingSummary(client.id, companyId ?? 0)
        .then(summary => setBillingSummary(summary))
        .catch(() => setBillingSummary(null))
        .finally(() => setBillingSummaryLoading(false));
      return;
    }

    if (!companyId) {
      // No company context — fall back to basic cached data
      setEnrichedClient(client);
      return;
    }

    setLoading(true);
    setError(null);

    // Fetch billing summary in parallel with financials
    setBillingSummaryLoading(true);
    fetchClientBillingSummary(client.id, companyId)
      .then(summary => {
        if (summary.isSuccess === false || typeof summary.errorMessage === 'string') {
          setBillingSummary(null);
        } else {
          setBillingSummary(summary);
        }
      })
      .catch(() => setBillingSummary(null))
      .finally(() => setBillingSummaryLoading(false));

    fetchClientFinancials(client.id, companyId)
      .then(financials => {
        // The backend returns isSuccess / errorMessage when the client cannot
        // be found in the Finance System (e.g. deleted customer).  Detect this
        // directly here rather than relying on throw/catch propagation.
        const raw = financials as Record<string, unknown>;
        if (raw.isSuccess === false || typeof raw.errorMessage === 'string') {
          const message = typeof raw.errorMessage === 'string'
            ? raw.errorMessage
            : 'This client could not be found in the Finance System';
          setError(message);
          setEnrichedClient(client);
          return;
        }

        // Strip backend-only metadata fields before merging with client
        const clientFields = { ...raw };
        delete clientFields.isSuccess;
        delete clientFields.errorMessage;
        setEnrichedClient({ ...client, ...clientFields } as Client);
      })
      .catch(() => {
        setError('Unable to load Finance System details');
        setEnrichedClient(client);
      })
      .finally(() => setLoading(false));
  }, [open, client?.id, companyId]);

  // Use enrichedClient for rendering when available, fall back to client prop
  const displayClient = enrichedClient ?? client;

  const isOnStop =
    displayClient?.accountStatus === 'on-stop' || displayClient?.accountStatus === 'suspended';

  const deliverySameAsInvoice = addressesMatch(
    displayClient?.invoiceAddress,
    displayClient?.deliveryAddress,
  );

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0 gap-0">
        {/* ── Header ─────────────────────────────────────────────── */}
        <SheetHeader className="px-6 py-5 border-b">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 shrink-0">
              <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">
                {displayClient ? getClientInitials(displayClient.name) : '?'}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <SheetTitle className="text-base leading-tight truncate">
                {displayClient?.name ?? '—'}
              </SheetTitle>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {displayClient?.code && (
                  <span className="text-xs text-muted-foreground font-mono">{displayClient.code}</span>
                )}
                {displayClient?.accountStatus && getAccountStatusBadge(displayClient.accountStatus)}
              </div>
            </div>
          </div>
        </SheetHeader>

        {/* ── Scrollable body ────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">

          {/* On Stop / Suspended warning */}
          {isOnStop && (
            <Alert variant="warning">
              <AlertTitle className="font-semibold">Account On Stop</AlertTitle>
              <AlertDescription>
                This account is currently{' '}
                <span className="font-semibold">
                  {displayClient?.accountStatus === 'on-stop' ? 'ON STOP' : 'SUSPENDED'}
                </span>{' '}
                in the Finance System. Contact Finance before proceeding with any invoicing
                or scope changes.
              </AlertDescription>
            </Alert>
          )}

          {/* Finance System notice — shown when data cannot be loaded */}
          {error && (
            <Alert variant="default" className="border-warning-200 bg-warning-50 text-warning-900">
              <div className="flex gap-2">
                <Info className="h-4 w-4 mt-0.5 shrink-0 text-warning-600" />
                <div className="space-y-1">
                  <AlertTitle className="text-sm font-semibold leading-tight">Finance System data unavailable</AlertTitle>
                  <AlertDescription className="text-xs leading-relaxed">
                    {error}
                  </AlertDescription>
                  <p className="text-xs text-warning-700 leading-relaxed">
                    Only basic project-level client information is shown below. For full financial
                    details, please verify this client still exists in the Finance System.
                  </p>
                </div>
              </div>
            </Alert>
          )}

          {/* Loading skeleton while fetching Finance System details */}
          {loading && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-4 w-40" />
              </div>
              <Separator />
              <div className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-44" />
                <Skeleton className="h-4 w-40" />
              </div>
              <Separator />
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            </div>
          )}

          {/* ── Detail sections (hidden while loading) ──────────── */}
          {!loading && (<>

          {/* ── Overview ──────────────────────────────────────────── */}
          <div className="space-y-3">
            <SectionHeading label="Overview" />
            {displayClient?.accountManagerName && (
              <InfoRow label="Account Manager" value={displayClient.accountManagerName} />
            )}
            {displayClient?.currencyCode && (
              <InfoRow label="Currency" value={displayClient.currencyCode} />
            )}
            {displayClient?.dueDateType && (
              <InfoRow label="Due Date" value={formatDueDate(displayClient.dueDateType, displayClient.dueDateDays)} />
            )}
            {displayClient?.anticipatedPaymentDays != null && (
              <InfoRow
                label="Anticipated Payment"
                value={`${displayClient.anticipatedPaymentDays} days ${displayClient.anticipatedPaymentType === 'from-due-date' ? 'from due date' : 'from invoice date'}`}
              />
            )}

            {displayClient?.vatNumber && (
              <InfoRow label="VAT Number" value={displayClient.vatNumber} />
            )}
          </div>

          <Separator />

          {/* ── Financial Standing ───────────────────────────────── */}
          {(displayClient?.creditLimit != null ||
            displayClient?.currentBalance != null ||
            displayClient?.overdueBalance != null) && (
            <>
              <div className="space-y-3">
                <SectionHeading label="Financial Standing" />
                {displayClient?.creditLimit != null && (
                  <InfoRow
                    label="Credit Limit"
                    value={formatCurrencyValue(displayClient.creditLimit, displayClient.currencyCode)}
                  />
                )}
                {displayClient?.currentBalance != null && (
                  <InfoRow
                    label="Current Balance"
                    value={formatCurrencyValue(displayClient.currentBalance, displayClient.currencyCode)}
                  />
                )}
                {displayClient?.overdueBalance != null && (
                  <InfoRow
                    label="Overdue Balance"
                    value={
                      <span
                        className={
                          displayClient.overdueBalance > 0
                            ? 'text-destructive font-semibold'
                            : 'text-success-600'
                        }
                      >
                        {displayClient.overdueBalance > 0
                          ? formatCurrencyValue(displayClient.overdueBalance, displayClient.currencyCode)
                          : '£0 ✓'}
                      </span>
                    }
                  />
                )}
              </div>
              <Separator />
            </>
          )}

          {/* ── Outstanding Balance ──────────────────────────────── */}
          {billingSummaryLoading ? (
            <>
              <div className="space-y-3">
                <SectionHeading label="Outstanding Balance" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-44" />
                <Skeleton className="h-4 w-36" />
              </div>
              <Separator />
            </>
          ) : billingSummary ? (() => {
            const { outstandingTotal, overdueCount, oldestUnpaidDate, currencyCode } = billingSummary;
            const hasOutstanding = outstandingTotal > 0;

            return (
              <>
                <div className="space-y-3">
                  <SectionHeading label="Outstanding Balance" />

                  <InfoRow
                    label="Outstanding"
                    value={
                      <span className={hasOutstanding ? 'text-warning-700 font-semibold' : 'text-success-600 font-semibold'}>
                        {hasOutstanding
                          ? formatCurrencyValue(outstandingTotal, currencyCode)
                          : '£0 — fully paid ✓'}
                      </span>
                    }
                  />

                  {hasOutstanding && (
                    <InfoRow
                      label="Overdue invoices"
                      value={
                        overdueCount > 0 ? (
                          <span className="flex items-center gap-1 text-destructive font-medium">
                            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                            {overdueCount} invoice{overdueCount !== 1 ? 's' : ''} overdue
                          </span>
                        ) : (
                          <span className="text-success-600 flex items-center gap-1">
                            <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                            None overdue
                          </span>
                        )
                      }
                    />
                  )}

                  {hasOutstanding && oldestUnpaidDate && (
                    <InfoRow
                      label="Oldest unpaid"
                      value={
                        <span className="text-muted-foreground">
                          {new Date(oldestUnpaidDate).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </span>
                      }
                    />
                  )}

                  {!hasOutstanding && (
                    <div className="flex items-center gap-2 text-xs text-success-700 bg-success-50 rounded-md px-3 py-2 border border-success-200">
                      <Receipt className="h-3.5 w-3.5 shrink-0" />
                      All invoices settled — no outstanding balance.
                    </div>
                  )}
                </div>
                <Separator />
              </>
            );
          })() : (
            <>
              <div className="space-y-3">
                <SectionHeading label="Outstanding Balance" />
                <p className="text-xs text-muted-foreground">Unable to load outstanding balance</p>
              </div>
              <Separator />
            </>
          )}

          {/* ── Addresses ────────────────────────────────────────── */}
          {(displayClient?.invoiceAddress || displayClient?.deliveryAddress) && (
            <>
              <div className="space-y-3">
                <SectionHeading label="Addresses" />

                {displayClient?.invoiceAddress && (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5" />
                      Invoice Address
                    </div>
                    <div className="pl-5">
                      <AddressBlock address={displayClient.invoiceAddress} />
                    </div>
                  </div>
                )}

                {displayClient?.deliveryAddress && (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5" />
                      Delivery Address
                    </div>
                    <div className="pl-5">
                      {deliverySameAsInvoice ? (
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <CheckCircle2 className="h-3.5 w-3.5 text-success-600 shrink-0" />
                          Same as Invoice Address
                        </div>
                      ) : (
                        <AddressBlock address={displayClient.deliveryAddress} />
                      )}
                    </div>
                  </div>
                )}
              </div>
              <Separator />
            </>
          )}

          {/* ── Contacts ─────────────────────────────────────────── */}
          {displayClient?.contacts && displayClient.contacts.length > 0 && (
            <>
              <div className="space-y-3">
                <SectionHeading label="Contacts" />
                {displayClient.contacts.map((contact, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <User className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{contact.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {CONTACT_ROLE_LABELS[contact.role] ?? contact.role}
                        </span>
                      </div>
                      {contact.email && (
                        <a
                          href={`mailto:${contact.email}`}
                          className="text-xs text-primary hover:underline truncate block"
                        >
                          {contact.email}
                        </a>
                      )}
                      {contact.phone && (
                        <a
                          href={`tel:${contact.phone}`}
                          className="text-xs text-muted-foreground hover:underline"
                        >
                          {contact.phone}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <Separator />
            </>
          )}

          {/* ── Additional Information (collapsible) ───────────────────────── */}
          {/* Sort key and custom fields are pulled from the Finance System.   */}
          {/* customFields is a key/value map of user-defined character (UDC)  */}
          {/* fields configured in the Finance System — display all entries    */}
          {/* dynamically so no frontend changes are needed when fields change.*/}
          {(displayClient?.sortKey || (displayClient?.customFields && Object.keys(displayClient.customFields).length > 0)) && (
            <Collapsible open={financeCodesOpen} onOpenChange={setFinanceCodesOpen}>
              <CollapsibleTrigger className="flex items-center justify-between w-full py-1 group">
                <SectionHeading label="Additional Information" />
                <ChevronDown
                  className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                    financeCodesOpen ? 'rotate-180' : ''
                  }`}
                />
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-3 pt-2">
                {/* Sort key — Finance System grouping/reporting key */}
                {displayClient?.sortKey && (
                  <InfoRow label="Sort Key" value={
                    <span className="font-mono text-xs">{displayClient.sortKey}</span>
                  } />
                )}
                {/* User-defined character (UDC) fields — pulled dynamically from Finance System */}
                {displayClient?.customFields && Object.entries(displayClient.customFields).map(([key, value]) => (
                  <InfoRow key={key} label={key} value={value} />
                ))}
              </CollapsibleContent>
            </Collapsible>
          )}

          </>)}

          {/* Spacer at the bottom so last item isn't flush with edge */}
          <div className="h-4" />
        </div>

      </SheetContent>
    </Sheet>
  );
}
